import { Box, Group, Paper, Stack, Switch, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { BulkAssessRow } from './BulkAssessRow'
import { CurriculumBreadcrumb } from './CurriculumBreadcrumb'
import { StudentAssessmentRow } from './StudentAssessmentRow'
import type { AssessmentItem, DrawerStudent } from '../types'

interface AssessmentItemCardProps {
  item: AssessmentItem
  itemKey: string
  visibleStudents: DrawerStudent[]
  showDeliverableColumn: boolean
  showActivityNameInDeliverable: boolean
  onStudentScoreChange: (studentId: string, score: number) => void
  onBulkScore: (score: number) => void
  onToggleEnabled: (enabled: boolean) => void
  /** Toggles per-student `do_not_assess`. */
  onToggleStudentEnabled: (studentId: string, enabled: boolean) => void
}

/**
 * Card per curriculum item: green/blue left border, breadcrumb header, "Enable
 * assessment" switch, optional bulk-assess row, and one StudentAssessmentRow
 * per visible student.
 *
 * The card collapses to a disabled state when `item.enabled === false` — the
 * derived flag set by the service when every visible student has
 * `do_not_assess: true`. In that state the switch sits OFF in the header, the
 * bulk row and student rows are hidden, and a message replaces them.
 */
export function AssessmentItemCard({
  item,
  itemKey,
  visibleStudents,
  showDeliverableColumn,
  showActivityNameInDeliverable,
  onStudentScoreChange,
  onBulkScore,
  onToggleEnabled,
  onToggleStudentEnabled,
}: AssessmentItemCardProps) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery('(max-width: 72em)')
  const color = item.curriculumKind === 'content' ? 'green' : 'blue'

  // The item-level switch only shows for multi-student selections (AR-828) —
  // with a single student the per-row switch covers it. Exception: a disabled
  // item hides its rows, so the header switch must stay as the only way to
  // re-enable it.
  const showItemSwitch = visibleStudents.length > 1 || !item.enabled

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      style={{
        borderLeftColor: `var(--mantine-color-${item.enabled ? color : 'gray'}-6)`,
        borderLeftWidth: 4,
        opacity: item.enabled ? 1 : 0.65,
      }}
    >
      <Stack spacing="md">
        {showItemSwitch && isMobile && (
          <Switch
            checked={item.enabled}
            onChange={e => onToggleEnabled(e.currentTarget.checked)}
            size="sm"
            label={t('arc_teacher_assessment_drawer.enable_assessment')}
            color={color}
          />
        )}

        <Group position="apart" align="flex-start" noWrap>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <CurriculumBreadcrumb item={item} />
          </Box>
          {showItemSwitch && !isMobile && (
            <Switch
              checked={item.enabled}
              onChange={e => onToggleEnabled(e.currentTarget.checked)}
              size="sm"
              label={t('arc_teacher_assessment_drawer.enable_assessment')}
              color={color}
            />
          )}
        </Group>

        {!item.enabled && (
          <Text size="sm" color="dimmed" italic>
            {t('arc_teacher_assessment_drawer.item_disabled')}
          </Text>
        )}

        {item.enabled && visibleStudents.length > 1 && (
          <BulkAssessRow
            onChange={onBulkScore}
            showDeliverableColumn={showDeliverableColumn}
            color={color}
            rubrics={item.rubrics}
          />
        )}

        {item.enabled && (
          <Stack spacing="md">
            {visibleStudents.map(student => (
              <StudentAssessmentRow
                key={`${itemKey}:${student.id}`}
                item={item}
                student={student}
                showDeliverableColumn={showDeliverableColumn}
                showActivityNameInDeliverable={showActivityNameInDeliverable}
                onScoreChange={score => onStudentScoreChange(student.id, score)}
                onToggleStudentEnabled={enabled => onToggleStudentEnabled(student.id, enabled)}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}
